import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./styles/global-enhancements.css";
// Premium redesign design system (tokens + rf-* primitives). Loaded after the
// legacy sheets so its custom properties win, but it only styles the rf-*
// namespace so legacy components are never implicitly restyled.
import "./styles/rf-design-system.css";
import App from "./App";
import "bootstrap/dist/css/bootstrap.min.css";
// slick-carousel CSS moved next to the components that actually use it
// (BookingStepper, ServicesList) so it ships with their route chunk instead of
// the global entry.
import AppState from "../src/context/AppState";
import { CurrencyProvider } from "./context/CurrencyContext";
import { i18nReady } from "./components/MultiLang/i18n.js"
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store/store'; // Ensure correct import path
import { BrowserRouter as Router } from "react-router-dom";
import preloadVehicleHeroImage from "./utils/preloadVehicleHero";
import { initGA4 } from "./utils/ga4";

// GA4 (G-W3Q4FEM68C) is owned by src/utils/ga4.js — see that file's header.
//
// The previous comment here said GA4 was "loaded by Google Tag Manager and
// nothing else", while public/index.html said the opposite ("loaded directly
// by the app, do NOT add a GA4 tag to GTM"). Both files deferred to the other,
// so GA4 had no owner: the published container GTM-MVTPG3QM contains only a
// Google Ads tag (AW-17983919375), no GA4 configuration tag, and /g/collect
// was never called on any route. That is why GA4 reported "No data received
// from your website yet".
//
// initGA4() loads gtag.js on the same idle/first-interaction gate the GTM
// snippet uses, so it adds no render-blocking work. Do NOT also add a GA4
// Configuration tag in GTM, or every page_view is counted twice.
//
// Commerce events are unchanged: they go through
// src/SharedComponent/tracking.js -> window.dataLayer -> GTM -> Google Ads.
initGA4();

/* Vehicle pages: hint the album's LCP image before React mounts.
   The vehicle id is already in the URL and the id -> asset map is static, so
   the image can be discovered now instead of after /api/v1/car/<id> resolves —
   which on throttled mobile does not start until ~5.7s, behind the tag
   manager. This runs before createRoot so the hint is in <head> as early as
   the bundle can put it there. */
preloadVehicleHeroImage();

/* ── Start the current route's chunk download NOW ──────────────────────────
   The route components are lazy (see routers/Routers.js), so webpack only
   requests their chunks once React renders the route — and React does not
   render until the runtime translation file has resolved. Measured on a
   throttled phone: main.js finished at 2947ms, then the route chunks sat
   undiscovered until 3195ms and landed at 4200ms, with nothing on the network
   in between.

   Touching the same dynamic import here starts that download immediately, in
   parallel with the i18n fetch, instead of after it. It is the identical
   import specifier, so webpack dedupes it — React's later `lazy()` resolves
   from the in-flight promise rather than issuing a second request.

   Only the route the user actually asked for is warmed; nothing speculative.
   Failures are swallowed: this is a head start, never a dependency. */
(() => {
  try {
    const seg2 = (window.location.pathname || "").split("/")[2] || "";
    const p = seg2.toLowerCase();
    if (p === "" ) {
      import("./pages/HomeNew").catch(() => {});
    } else if (process.env.REACT_APP_SCOPED_SPEED === "true" && ["rent-a-car-marrakech", "rent-a-car-rabat", "airport-car-rental-morocco"].includes(p)) {
      import(/* webpackChunkName: "speed-landing" */ "./components/SEO/SeoLandingPage").catch(() => {});
    } else if (process.env.REACT_APP_SCOPED_SPEED === "true" && p === "blogs") {
      import("./routers/GuideRoutes").catch(() => {});
    } else if (p === "ourfleetlist") {
      import("./components/UI/OurFleetList").catch(() => {});
    } else if (p === "cars") {
      import("./pages/CarDetails").catch(() => {});
    }
  } catch { /* never block boot */ }
})();

const root = ReactDOM.createRoot(document.getElementById("root"));

const tree = (
  // <React.StrictMode>
    <Router>
    <AppState>
      <Provider store={store} >
      <PersistGate loading={null} persistor={persistor}>
      <CurrencyProvider>
      <App />
      </CurrencyProvider>
      </PersistGate>
      </Provider>
      </AppState>
    </Router>
    // </React.StrictMode>
);

// Translations are fetched at runtime rather than bundled (see
// MultiLang/i18n.js), so the first render waits until the active language is in
// memory. index.html preloads that file, so it is normally already cached by the
// time the bundle finishes booting and this resolves immediately. Rendering
// anyway on failure is deliberate: a missing translation file should degrade to
// untranslated keys, not a blank site.
// ── The build-time scaffolding ──────────────────────────────────────────────
// scripts/inline-hero.js injects a static copy of the above-the-fold chrome
// (and, on the fleet route, the first card) so the browser can paint the first
// screen without waiting for this bundle. It is removed once React's own
// render has been PAINTED — not before; see dropScaffolding below.
i18nReady.catch(() => {}).then(() => {
  const prepaint = document.getElementById("rf-prepaint");
  const fleetPrepaint = document.getElementById("rf-fleet-prepaint");
  const vdPrepaint = document.getElementById("rf-vd-prepaint");

  // ── Remove the scaffolding only AFTER React has actually painted ─────────
  //
  // This used to remove it in the same task as `root.render(tree)`, on the
  // assumption that "removal and render happen in the same task, so no frame
  // is painted in between". Measurement disproved that: React 18's render is
  // asynchronous, so at t~900ms on a throttled phone the DOM had NO hero at
  // all — the scaffolding was gone and React had not committed yet — and the
  // cold load showed a blank gap where the car had been. That is exactly the
  // "page looks unfinished" frame the owner reported.
  //
  // Two animation frames after render is the earliest point at which React's
  // commit has been painted. The scaffolding stays until then (identical
  // geometry, so nothing moves) and is dropped once the real hero is on
  // screen: a straight swap with no empty frame.
  //
  // The poster's decoded bitmap is handed over first. Tearing the <img> out
  // and letting React create a fresh one made Chrome treat the replacement as
  // a new, very slightly different LCP candidate (202690px^2 -> 202800px^2)
  // and report LCP at ~4.7s instead of the ~0.9s the user really experienced.
  const dropScaffolding = () => {
    if (prepaint) {
      const painted = prepaint.querySelector(".rf-hero__poster");
      if (painted && painted.currentSrc) {
        window.__RF_HERO_PAINTED__ = painted.currentSrc;
      }
      prepaint.remove();
    }
    if (fleetPrepaint) {
      const paintedCard = fleetPrepaint.querySelector(".rf-sc__img");
      if (paintedCard && paintedCard.currentSrc) {
        window.__RF_FLEET_PAINTED__ = paintedCard.currentSrc;
      }
      fleetPrepaint.remove();
    }
    if (vdPrepaint) {
      const paintedAlbum = vdPrepaint.querySelector(".rf-album__img");
      if (paintedAlbum && paintedAlbum.currentSrc) {
        window.__RF_VD_PAINTED__ = paintedAlbum.currentSrc;
      }
      vdPrepaint.remove();
    }
    // The prerendered SEO copy (scripts/prerender.js) carries this page's real
    // heading and body text for crawlers that never run JavaScript. Once
    // React has painted it would otherwise sit in the DOM twice over.
    document.getElementById("root-prerender")?.remove();
  };

  // Two animation frames after render is NOT enough, and I measured why: the
  // homepage route is lazy, so React's first commit is a Suspense fallback,
  // not the hero. Dropping the scaffolding then left a 700ms hole — the
  // scaffolding went at 2500ms and `section.rf-hero` only existed at 3200ms —
  // which is the "background/blank then the car appears" frame the owner
  // reported.
  //
  // So the trigger is the REAL HERO APPEARING, not a frame count: wait until
  // React has put its own `.rf-hero__media` in the DOM, then drop ours on the
  // next frame. A MutationObserver catches it whenever it lands, with a
  // ceiling so the scaffolding can never outlive a failed render, and a
  // fallback for the fleet route (whose own scaffolding is a card, not a
  // hero).
  const waitForReactHero = (done) => {
    // Which scaffolding is on this page decides what we must wait for. The
    // previous version OR-ed both conditions, so on the homepage the
    // "fleet cards exist" clause could satisfy it (the homepage renders
    // vehicle cards further down the page, outside any scaffolding) and the
    // hero was dropped a full second before React's own hero existed —
    // measured: DEL rf-prepaint at 3026ms, React's hero at 4022ms.
    //
    // Now each scaffolding waits for its own replacement, and nothing else.
    const replaced = () => {
      if (prepaint) {
        for (const h of document.querySelectorAll(".rf-hero__media")) {
          if (!prepaint.contains(h)) return true;
        }
        return false;
      }
      if (fleetPrepaint) {
        for (const c of document.querySelectorAll(".rf-showroom__grid > li")) {
          if (!fleetPrepaint.contains(c)) return true;
        }
        return false;
      }
      if (vdPrepaint) {
        // The album image React renders, not ours. Waiting for the <img> to
        // exist is not enough — it must have PAINTED, or the scaffold is torn
        // down while React's copy is still decoding and the car disappears
        // for a frame (the same defect the homepage had).
        for (const a of document.querySelectorAll(".rf-album__img")) {
          if (!vdPrepaint.contains(a) && a.complete && a.naturalWidth > 0) {
            return true;
          }
        }
        return false;
      }
      return true;
    };

    if (replaced()) return done();

    // Both declared before the observer that clears them: a `const`
    // referenced from the callback before its own initialiser has run is a
    // TDZ error, and this callback can fire during `observe()`.
    let ceiling = null;
    let poll = null;
    const obs = new MutationObserver(() => {
      if (replaced()) {
        obs.disconnect();
        if (ceiling) clearTimeout(ceiling);
        if (poll) clearInterval(poll);
        requestAnimationFrame(done);
      }
    });
    obs.observe(document.getElementById("root"), { childList: true, subtree: true });

    // A poll alongside the observer, because one of the conditions is not a
    // mutation: the vehicle album's `img.complete` flips when the image
    // finishes decoding, which fires no DOM mutation at all. Without this the
    // scaffold would sit until the 8s ceiling on every vehicle page.
    poll = setInterval(() => {
      if (replaced()) {
        clearInterval(poll);
        obs.disconnect();
        if (ceiling) clearTimeout(ceiling);
        requestAnimationFrame(done);
      }
    }, 100);

    // Never leave the scaffolding up forever: if React's tree never produces a
    // hero (an error boundary, a route with neither), drop it after 8s so the
    // page is not permanently showing static chrome over a live app.
    ceiling = setTimeout(() => {
      clearInterval(poll);
      obs.disconnect();
      done();
    }, 8000);
  };

  root.render(tree);
  waitForReactHero(dropScaffolding);
});
